import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { ContextSession } from "../../services/ContextSession";
import logoInjupemp from "../../img/LogoInjupemp2026.png";
import { obtenerReporteUsuariosDetalleEnrolamiento } from "./enrolamientoApi";
import {
  formatUsuarioReporte,
  formatIsoDateToEs,
  getCurrentMonthDateRange,
  isSinOficinaReporte,
  openHtmlPrintWindow,
  resolveReporteOfficeName,
  safeText,
  validateReportDateRange,
} from "./reporteriaUtils";

const OFICINA_ORIGEN_SORT = {
  HISTORICO: 0,
  ACTUAL: 1,
  SIN_DATOS: 2,
};

const resolveOficinaMeta = (item = {}) => {
  const origen = safeText(item?.OficinaOrigen).trim().toUpperCase();
  const nombre = resolveReporteOfficeName({
    oficinaNombre: item?.OficinaNombre,
    oficinaOrigen: item?.OficinaOrigen,
    oficinaId: item?.OficinaId,
    oficinaKey: item?.OficinaKey,
  });
  return {
    origen,
    nombre,
    nombreUpper: safeText(nombre).trim().toUpperCase(),
    idText: safeText(item?.OficinaId).trim(),
    keyText: safeText(item?.OficinaKey).trim().toUpperCase(),
  };
};

const compareOficinaMeta = (aMeta, bMeta) => {
  const aOrigenOrder =
    OFICINA_ORIGEN_SORT[aMeta?.origen] !== undefined
      ? OFICINA_ORIGEN_SORT[aMeta?.origen]
      : 9;
  const bOrigenOrder =
    OFICINA_ORIGEN_SORT[bMeta?.origen] !== undefined
      ? OFICINA_ORIGEN_SORT[bMeta?.origen]
      : 9;

  if (aOrigenOrder !== bOrigenOrder) return aOrigenOrder - bOrigenOrder;

  const byName = safeText(aMeta?.nombreUpper).localeCompare(
    safeText(bMeta?.nombreUpper),
    "es",
  );
  if (byName !== 0) return byName;

  const aId = Number(aMeta?.idText || 0);
  const bId = Number(bMeta?.idText || 0);
  if (Number.isFinite(aId) && Number.isFinite(bId) && aId !== bId) {
    return aId - bId;
  }

  return safeText(aMeta?.keyText).localeCompare(safeText(bMeta?.keyText), "es");
};

const buildItemsFiltrados = (payload) => {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items.filter(
    (item) =>
      !isSinOficinaReporte({
        oficinaNombre: item?.OficinaNombre,
        oficinaOrigen: item?.OficinaOrigen,
        oficinaId: item?.OficinaId,
        oficinaKey: item?.OficinaKey,
      }),
  );
};

const compareItemsByOficina = (aItem, bItem) => {
  const oficinaCompare = compareOficinaMeta(
    resolveOficinaMeta(aItem),
    resolveOficinaMeta(bItem),
  );
  if (oficinaCompare !== 0) return oficinaCompare;

  const usuarioCompare = safeText(aItem?.UsuarioRegistro)
    .toUpperCase()
    .localeCompare(safeText(bItem?.UsuarioRegistro).toUpperCase(), "es");
  if (usuarioCompare !== 0) return usuarioCompare;

  const fechaCompare = (safeText(aItem?.FechaRegistroIso) || "9999-12-31 23:59:59").localeCompare(
    safeText(bItem?.FechaRegistroIso) || "9999-12-31 23:59:59",
  );
  if (fechaCompare !== 0) return fechaCompare;

  return safeText(aItem?.DocumentoNumero).localeCompare(
    safeText(bItem?.DocumentoNumero),
    "es",
  );
};

const buildGruposPorOficina = (payload) => {
  const itemsOrdenados = [...buildItemsFiltrados(payload)].sort(compareItemsByOficina);
  const gruposMap = new Map();

  itemsOrdenados.forEach((item) => {
    const oficinaMeta = resolveOficinaMeta(item);
    const groupKey =
      safeText(item?.OficinaKey).trim() ||
      `${oficinaMeta.origen || "SIN_DATOS"}-${safeText(item?.OficinaId).trim() || "0"}-${oficinaMeta.nombreUpper}`;

    if (!gruposMap.has(groupKey)) {
      gruposMap.set(groupKey, {
        OficinaKey: groupKey,
        OficinaOrigen: oficinaMeta.origen,
        OficinaId: item?.OficinaId ?? "",
        OficinaNombre: item?.OficinaNombre ?? "",
        OficinaNombreResolved: oficinaMeta.nombre,
        Total: 0,
        Registros: [],
      });
    }

    const group = gruposMap.get(groupKey);
    group.Registros.push(item);
    group.Total += 1;
  });

  return Array.from(gruposMap.values()).sort((aGroup, bGroup) =>
    compareOficinaMeta(
      resolveOficinaMeta({
        OficinaOrigen: aGroup?.OficinaOrigen,
        OficinaNombre: aGroup?.OficinaNombre,
        OficinaId: aGroup?.OficinaId,
        OficinaKey: aGroup?.OficinaKey,
      }),
      resolveOficinaMeta({
        OficinaOrigen: bGroup?.OficinaOrigen,
        OficinaNombre: bGroup?.OficinaNombre,
        OficinaId: bGroup?.OficinaId,
        OficinaKey: bGroup?.OficinaKey,
      }),
    ),
  );
};

const buildPrintHtml = (payload) => {
  const filtros = payload?.filtros || {};
  const grupos = buildGruposPorOficina(payload);
  const totalRegistros = grupos.reduce((acc, group) => acc + Number(group?.Total || 0), 0);
  const gruposHtml = grupos
    .map((group) => {
      const registros = Array.isArray(group?.Registros) ? group.Registros : [];
      const rowHtml = registros
        .map(
          (row) => `
        <tr>
          <td>${safeText(row?.FechaRegistro)}</td>
          <td>${safeText(row?.DocumentoNumero)}</td>
          <td>${safeText(row?.NombreCompleto)}</td>
          <td>${formatUsuarioReporte(row?.UsuarioRegistro, "SIN USUARIO")}</td>
        </tr>
      `,
        )
        .join("");

      return `
        <section class="group">
          <p class="office"><b>Oficina:</b> ${safeText(group?.OficinaNombreResolved)} (${safeText(group?.OficinaOrigen)}) - <b>Total:</b> ${Number(group?.Total || 0)}</p>
          <table>
            <thead>
              <tr>
                <th>Fecha Registro</th>
                <th>Identificación</th>
                <th>Nombre</th>
                <th>Usuario</th>
              </tr>
            </thead>
            <tbody>
              ${rowHtml}
            </tbody>
          </table>
        </section>
      `;
    })
    .join("");

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Reporte de Usuarios Detalle</title>
    <style>
      @page { size: auto; margin: 12mm; }
      body { font-family: Arial, sans-serif; color: #111; margin: 20px; font-size: 12px; }
      .actions { text-align: right; margin-bottom: 10px; }
      .btn { border: 0; border-radius: 4px; background: #19498c; color: #fff; padding: 8px 14px; cursor: pointer; }
      .header { display: flex; align-items: center; gap: 18px; margin-bottom: 6px; }
      .logo { height: 60px; width: auto; }
      .title { font-size: 22px; font-weight: 700; margin: 0; }
      .date-row { display: flex; gap: 40px; margin-bottom: 12px; font-size: 18px; }
      .office { margin: 6px 0; font-size: 15px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 12px; }
      th, td { border: 1px solid #222; padding: 4px 6px; font-size: 12px; text-align: left; }
      th { background: #f1f4f9; }
      .group { margin-bottom: 12px; break-inside: auto; page-break-inside: auto; }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr { break-inside: avoid; page-break-inside: avoid; }
      .total { font-size: 18px; font-weight: 700; text-align: right; margin-top: 10px; }
      @media print {
        body { margin: 0; }
        .actions { display: none; }
      }
    </style>
  </head>
  <body>
    <div class="actions">
      <button class="btn" onclick="window.print()">Imprimir</button>
    </div>
    <div class="header">
      <img class="logo" src="${logoInjupemp}" alt="INJUPEMP" />
      <h1 class="title">Reporte de Usuarios Detalle</h1>
    </div>
    <div class="date-row">
      <div><b>Fecha Inicial:</b> ${formatIsoDateToEs(filtros?.fecha_inicio)}</div>
      <div><b>Fecha Final:</b> ${formatIsoDateToEs(filtros?.fecha_fin)}</div>
    </div>
    ${gruposHtml}
    <p class="total">Total de Personas Registradas: ${totalRegistros}</p>
  </body>
</html>`;
};

const buildExcelRows = (payload) => {
  const grupos = buildGruposPorOficina(payload);
  return grupos.flatMap((group) => {
    const registros = Array.isArray(group?.Registros) ? group.Registros : [];
    return registros.map((row) => ({
      Oficina: `${safeText(group?.OficinaNombreResolved)} (${safeText(group?.OficinaOrigen)})`,
      "Fecha Registro": safeText(row?.FechaRegistro),
      Identificacion: safeText(row?.DocumentoNumero),
      Nombre: safeText(row?.NombreCompleto),
      Usuario: formatUsuarioReporte(row?.UsuarioRegistro, "SIN USUARIO"),
    }));
  });
};

const toSafeFileDate = (value) => {
  const raw = safeText(value) || "sin-fecha";
  return raw.replace(/[^\dA-Za-z-]/g, "-");
};

export default function ReporteUsuariosDetalleEnrolamiento() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";

  const currentMonthRange = getCurrentMonthDateRange();
  const [fechaInicio, setFechaInicio] = useState(currentMonthRange.fechaInicio);
  const [fechaFin, setFechaFin] = useState(currentMonthRange.fechaFin);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reporte, setReporte] = useState(null);

  const handleConsultar = async () => {
    const validationError = validateReportDateRange(fechaInicio, fechaFin);
    if (validationError) {
      setError(validationError);
      setReporte(null);
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await obtenerReporteUsuariosDetalleEnrolamiento(authToken, {
        fechaInicio,
        fechaFin,
      });
      setReporte(data || null);
    } catch (err) {
      setError(err?.message || "No se pudo generar el reporte de usuarios detalle.");
      setReporte(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImprimir = () => {
    if (!reporte) return;
    openHtmlPrintWindow(buildPrintHtml(reporte));
  };

  const handleExportarExcel = () => {
    if (!reporte) return;
    const rows = buildExcelRows(reporte);
    if (!rows.length) return;

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [
      { wch: 32 },
      { wch: 20 },
      { wch: 18 },
      { wch: 45 },
      { wch: 36 },
    ];
    XLSX.utils.book_append_sheet(workbook, worksheet, "UsuariosDetalle");

    const fechaInicio = toSafeFileDate(reporte?.filtros?.fecha_inicio);
    const fechaFin = toSafeFileDate(reporte?.filtros?.fecha_fin);
    XLSX.writeFile(workbook, `reporte_usuarios_detalle_${fechaInicio}_a_${fechaFin}.xlsx`);
  };

  const grupos = buildGruposPorOficina(reporte);
  const totalRegistrosVisibles = grupos.reduce(
    (acc, group) => acc + Number(group?.Total || 0),
    0,
  );

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 2.5, borderRadius: 3, border: "1px solid #dce7f7" }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#19498c", mb: 2 }}>
          Reporte de Usuarios Detalle
        </Typography>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Fecha inicio"
            type="date"
            size="small"
            value={fechaInicio}
            onChange={(event) => setFechaInicio(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: fechaFin }}
          />
          <TextField
            label="Fecha final"
            type="date"
            size="small"
            value={fechaFin}
            onChange={(event) => setFechaFin(event.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: fechaInicio }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleConsultar}
            disabled={loading}
            sx={{ textTransform: "none", fontWeight: 700, backgroundColor: "#19498c" }}
          >
            {loading ? "Consultando..." : "Consultar"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={handleImprimir}
            disabled={!reporte}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Imprimir
          </Button>
          <Button
            variant="outlined"
            color="success"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportarExcel}
            disabled={!reporte || !grupos.length}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Excel
          </Button>
        </Stack>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {reporte ? (
          <>
            <Typography sx={{ color: "#4f5f77", fontSize: 13, mb: 2 }}>
              Período: {formatIsoDateToEs(reporte?.filtros?.fecha_inicio)} a {formatIsoDateToEs(reporte?.filtros?.fecha_fin)}.{" "}
              Total registros: <b>{totalRegistrosVisibles}</b>
            </Typography>

            {grupos.map((group) => {
              const registros = Array.isArray(group?.Registros) ? group.Registros : [];
              return (
                <Paper key={group?.OficinaKey} variant="outlined" sx={{ p: 1.5, mb: 2 }}>
                  <Typography sx={{ fontWeight: 700, color: "#1a2f4d", mb: 0.8 }}>
                    Oficina: {group?.OficinaNombreResolved} ({group?.OficinaOrigen}) - Total:{" "}
                    {Number(group?.Total || 0)}
                  </Typography>
                  <Box sx={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>
                            Fecha Registro
                          </th>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>
                            Identificación
                          </th>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>Nombre</th>
                          <th style={{ border: "1px solid #c5cedd", padding: 6, textAlign: "left" }}>Usuario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registros.map((row, index) => (
                          <tr key={`${row?.PersonaId || row?.DocumentoNumero || ""}-${index}`}>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.FechaRegistro || ""}</td>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.DocumentoNumero || ""}</td>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>{row?.NombreCompleto || ""}</td>
                            <td style={{ border: "1px solid #e0e5ef", padding: 6 }}>
                              {formatUsuarioReporte(row?.UsuarioRegistro, "SIN USUARIO")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Paper>
              );
            })}

            <Typography sx={{ textAlign: "right", fontWeight: 700, mt: 1, fontSize: 18 }}>
              Total de Personas Registradas: {totalRegistrosVisibles}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: "#5a6a80" }}>
            Seleccione la fecha inicial y la fecha final para generar el reporte.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
